import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { ClientsRepository } from "@/api/clients/repository";
import { ClientsService } from "@/api/clients/service";
import { useAppStore } from "@/store/app/slice";
import type { IClient } from "@/types/users-clients";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

export const useFetchClientProfile = () => {
  const abortController = useRef<AbortController | null>(null);
  const user = useAppStore((s) => s.user);
  const [profile, setProfile] = useState<IClient | null>(null);

  const fetchProfile = useCallback(() => {
    if (!user) {
      return;
    }

    const clientsRepository = new ClientsRepository(new ClientsService());

    abortController.current?.abort();
    abortController.current = new AbortController();
    const signal = abortController.current.signal;

    clientsRepository
      .getProfileByUserId(
        {
          userId: user.id,
        },
        { signal },
      )
      .then((response) => {
        setProfile(response);
      })
      .catch(serverErrorHandler());
  }, [user]);

  useEffect(() => {
    const debouncedFetch = debounce(fetchProfile, 300);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
      abortController.current?.abort();
    };
  }, [fetchProfile]);

  return {
    fetchProfile,
    profile,
  };
};
